import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setSnackbar} from '../store/reducers/globals';
import {useTranslation} from 'react-i18next';
import {getConfig} from '../util/configUtil';
import {user} from '../store/reducers/user';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({children}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Selectors
    const userObject = useSelector(user);

    // WebSocket reference
    const ws = useRef(null);
    const isConnecting = useRef(false);
    const [isConnected, setIsConnected] = useState(false);

    // State to manage messages
    const [pendingMessages, setPendingMessages] = useState([]);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        if (!ws.current) {
            connectWebSocket();
        }
    }, []);

    /**
     * Effect hook that sends pending messages when the length of pendingMessages changes.
     * If the WebSocket is not open, it attempts to reconnect.
     */
    useEffect(() => {
        if (isConnected && pendingMessages.length > 0) {
            pendingMessages.forEach(message => ws.current?.send(JSON.stringify(message)));
            setPendingMessages([]);
        }
    }, [isConnected, pendingMessages.length]);

    useEffect(() => {
        const sendData = async (e) => {
            if (e.data instanceof Blob) {
                return await e.data.text();
            } else {
                return e.data;
            }
        };
        if (isConnected && ws.current) {
            ws.current.onmessage = (event) => {
                sendData(event).then((data) => {
                    handleMessage(data);
                });
            };
        }
    }, [isConnected]);

    /** 
     * Effect hook that pings the WebSocket every 25 seconds if connected.
     * This helps keep the connection alive and checks if the WebSocket is still responsive.
     */
    useEffect(() => {
      let interval;

      if (isConnected) {
        interval = setInterval(ping, 25000);
      }

      return () => {
        if (interval) clearInterval(interval);
      };
    }, [userObject, isConnected]);

    /**
     * Establishes a WebSocket connection.
     * If the WebSocket is already connecting or open, it does nothing.
     */
    const connectWebSocket = () => {
        if (isConnected || isConnecting.current || (ws.current?.readyState === WebSocket.OPEN || ws.current?.readyState === WebSocket.CONNECTING)) {
            return;
        }
        isConnecting.current = true;

        const webSocketUrl = getConfig('webSocketUrl');
        if (!webSocketUrl) {
            console.error('WebSocket URL is not available');
            return;
        }

        const webSocket = new WebSocket(webSocketUrl);
        ws.current = webSocket;

        webSocket.onopen = () => {
            console.log('✅ WebSocket connected');
            setIsConnected(true);

            const token = window.auth?.token || window.keycloak?.token;
            if (token) webSocket.send(token);
            pendingMessages.forEach(message => webSocket.send(JSON.stringify(message)));
            setPendingMessages([]);
        };

        webSocket.onclose = (e) => {
            console.log('❌ WebSocket closed', e);
            setIsConnected(false);
            isConnecting.current = false;
            setTimeout(() => {
                if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
                    connectWebSocket();
                }
            }, 1000);
        };

        webSocket.onerror = (error) => {
            console.error('⚠️ WebSocket error', error);
            isConnecting.current = false;
            dispatch(setSnackbar({type: 'error', message: t('Failed to connect to WebSocket')}));
        };
    };

    /**
     * Retrieves the user object from the Redux store.
     *
     * @returns {Object} The user object containing user details.
     */
    const getUserObject = () => {
        return {
            __id: userObject.__id,
            userName: userObject.userName,
            familyName: userObject.familyName,
            firstName: userObject.firstName,
            hash: userObject.hash,
        };
    };

    /**
     * Sends a message through the WebSocket connection.
     *
     * @param {Object} message - The message to send.
     * @param {string|string[]} wsChannels - The WebSocket channels to send the message to.
     * @param {string|null} [id=null] - An optional ID for the message.
     */
    const sendMessage = (message, wsChannels, id = null) => {
        const channels = [].concat(wsChannels);
        if (!channels.length) {
            console.warn('No channels provided for subscription/unsubscription');
            return;
        }

        if (!message) {
            console.warn('No message provided to send');
            return;
        }

        const msg = {
            cmd: 'msg',
            data: {
                ...message,
                user: getUserObject(),
            },
            channels,
        };

        if (id) msg.id = id;

        if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify(msg));
        } else {
            console.warn('WebSocket is not open, queuing message');
            setPendingMessages(prev => [...prev, msg]);
        }
    };

    /**
     * Manages subscription or unsubscription to WebSocket channels.
     *
     * @param {string|string[]} wsChannels - The WebSocket channels to subscribe or unsubscribe to.
     * @param {boolean} [subscribe=true] - Whether to subscribe (true) or unsubscribe (false).
     */
    const manageSubscription = (wsChannels, subscribe = true) => {
        const channels = [].concat(wsChannels);

        if (!channels.length) {
            console.warn('No channels provided for subscription/unsubscription');
            return;
        }

        const msg = {cmd: subscribe ? 'sub' : 'unsub', data: null, channels};

        if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify(msg));
        } else {
            console.warn('WebSocket is not open, queuing message');
            setPendingMessages(prev => [...prev, msg]);
        }
    };

    const handleMessage = (message) => {
        if (message) {
            try {
                setMessage(JSON.parse(message));
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
                setMessage(message);
            } finally {
                // Clear the message after it has been processed
                setTimeout(() => setMessage(null), 0);
            }
        }
    };

    const ping = useCallback(() => {
      const msg = {
        cmd: "ping",
        data: {
          user: getUserObject(),
        },
      };
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify(msg));
      }
    }, [userObject]);

    return (
        <WebSocketContext.Provider
            value={{
                isConnected,
                sendMessage,
                manageSubscription,
                message,
            }}
        >
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => {
    return useContext(WebSocketContext);
};